import type { CSSProperties, ReactNode } from "react";
import { useMemo } from "react";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import { UIProvider, useUiColorOptional } from "../utils/UIContext";
import type { UISize } from "../utils/UIContext";
import { SIZE_FONT, type UiColor } from "../utils/colors";
import { type UiPaletteKey, resolveUiPalette } from "../utils/uiPalette";

export type ProgressSize = UISize;

export type ProgressProps = {
  percent?: number | string;
  value?: number | string;
  total?: number | string;
  indicating?: boolean;
  color?: UiColor;
  size?: ProgressSize;
  /** Show a label on the bar: true → percent, or "percent" | "ratio" | "value". */
  progress?: boolean | "percent" | "ratio" | "value";
  label?: ReactNode;
  attached?: "top" | "bottom";
  precision?: number;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  id?: string;
};

type StyleProps = { $size?: ProgressSize; $percent: number; $barColor: string };

const StyledProgressBar = styled.div`
  display: block;
  line-height: 1;
  position: absolute;
  left: 0;
  top: 0;
  min-width: 2em;
  border-radius: inherit;
  height: 100%;
  transition: width 0.3s ease;
`;

const StyledProgressBarLabel = styled.div`
  position: absolute;
  white-space: nowrap;
  width: auto;
  top: 50%;
  right: 0.5em;
  left: auto;
  bottom: auto;
  color: rgba(255, 255, 255, 0.7);
  text-shadow: none;
  margin-top: -0.5em;
  font-weight: bold;
  font-size: 0.75em;
`;

const StyledProgressLabel = styled.div`
  position: absolute;
  width: 100%;
  font-size: 1em;
  top: 100%;
  left: 0;
  text-align: center;
  margin-top: 0.2em;
  color: rgba(0, 0, 0, 0.87);
  font-weight: bold;
`;

const StyledProgress = styled.div<StyleProps>`
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border: none;
  padding: 0;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 0.3rem;
  height: ${({ $size }) => SIZE_FONT[$size || "medium"] || "1.14285714em"};
  font-size: ${({ $size }) => SIZE_FONT[$size || "medium"] || "1rem"};
  overflow: hidden;

  &.is-attached-top {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: auto;
    margin: 0;
    height: 0.35em;
    border-radius: 0.3rem 0.3rem 0 0;
  }

  &.is-attached-bottom {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    width: auto;
    margin: 0;
    height: 0.35em;
    border-radius: 0 0 0.3rem 0.3rem;
  }

  ${StyledProgressBar} {
    width: ${({ $percent }) => Math.min(100, Math.max(0, $percent))}%;
    background-color: ${({ $barColor }) => $barColor};
  }

  &.is-attached-top
  ${StyledProgressBar},
  &.is-attached-bottom
  ${StyledProgressBar} {
    min-width: 0;
  }

  &.is-indicating ${StyledProgressBar} {
    background-image: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.15) 25%,
      transparent 25%,
      transparent 50%,
      rgba(255, 255, 255, 0.15) 50%,
      rgba(255, 255, 255, 0.15) 75%,
      transparent 75%,
      transparent
    );
    background-size: 40px 40px;
    animation: sdo-progress-indicating 2s linear infinite;
  }

  @keyframes sdo-progress-indicating {
    0% {
      background-position: 0 0;
    }
    100% {
      background-position: 40px 0;
    }
  }
`;

function toNumber(v: number | string | undefined): number | undefined {
  if (v === undefined || v === null || v === "") {
    return undefined;
  }
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function formatProgressLabel(
  mode: boolean | "percent" | "ratio" | "value",
  percent: number,
  value?: number,
  total?: number,
  precision = 0,
): string {
  if (mode === "ratio" && value !== undefined && total !== undefined) {
    return `${value}/${total}`;
  }
  if (mode === "value" && value !== undefined) {
    return String(value);
  }
  const rounded =
    precision === 0 ? Math.round(percent) : Number(percent.toFixed(precision));
  return `${rounded}%`;
}

function ProgressBar(props: {
  id?: string;
  className?: string;
  style?: CSSProperties;
  size?: ProgressSize;
  indicating?: boolean;
  attached?: "top" | "bottom";
  percent: number;
  fallbackColor?: string;
  barLabel: string | null;
  belowLabel: ReactNode;
}) {
  const uiColor = useUiColorOptional();
  const barColor = uiColor?.bg || props.fallbackColor || "#2185d0";

  return (
    <StyledProgress
      id={props.id}
      className={cx(
        props.attached === "top" && "is-attached-top",
        props.attached === "bottom" && "is-attached-bottom",
        props.indicating && "is-indicating",
        props.className,
      )}
      style={props.style}
      $size={props.size}
      $percent={props.percent}
      $barColor={barColor}
      role="progressbar"
      aria-valuenow={Math.round(props.percent)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <StyledProgressBar>
        {props.barLabel != null ? (
          <StyledProgressBarLabel>{props.barLabel}</StyledProgressBarLabel>
        ) : null}
      </StyledProgressBar>
      {props.belowLabel != null && props.belowLabel !== false ? (
        <StyledProgressLabel>{props.belowLabel}</StyledProgressLabel>
      ) : null}
    </StyledProgress>
  );
}

const ProgressComponent = (props: ProgressProps) => {
  const {
    percent: percentProp,
    value: valueProp,
    total: totalProp,
    indicating,
    color,
    size,
    progress,
    label,
    attached,
    precision = 0,
    children,
    className,
    style,
    id,
  } = props;
  const theme = useTheme();
  const value = toNumber(valueProp);
  const total = toNumber(totalProp);
  let percent = toNumber(percentProp) ?? 0;
  if (
    (percentProp === undefined || percentProp === null) &&
    value !== undefined &&
    total !== undefined &&
    total > 0
  ) {
    percent = (value / total) * 100;
  }

  const showBarLabel = Boolean(progress);
  const barLabel = showBarLabel
    ? formatProgressLabel(
        progress === true ? "percent" : progress || "percent",
        percent,
        value,
        total,
        precision,
      )
    : null;

  const belowLabel = label !== undefined ? label : children;

  const palette = useMemo(() => {
    return resolveUiPalette(theme, (color ?? "primary") as UiPaletteKey);
  }, [color, theme]);

  const fallbackColor = theme.primaryColour || "#2185d0";

  const bar = (
    <ProgressBar
      id={id}
      className={className}
      style={style}
      size={size}
      indicating={indicating}
      attached={attached}
      percent={percent}
      fallbackColor={fallbackColor}
      barLabel={barLabel}
      belowLabel={belowLabel}
    />
  );

  return <UIProvider value={{ colors: palette }}>{bar}</UIProvider>;
};

export const Progress = styled(ProgressComponent)``;
